// src/components/elements/calendar/orm-calendar.tsx

import React, { useState, useMemo, useRef, useEffect } from "react";
import VlCalendarMonth from "./vl-calendar-month";
import { getToday, parseDate } from "../../../utils/datesUtils";
import { DAYS_SHORTCUTS, langTranslate } from "../../../utils/calculations";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { formatDate } from "../../../utils/formater";
import { useKeydownListener } from "../../../hooks/useKeydownListener";

type DayShortcut = (typeof DAYS_SHORTCUTS)[number];
type Language = "DE" | "EN";

interface VlCalendarProps {
  isSelected?: (date: string) => boolean;
  isDisabled?: (dateOrWeekday: string | number) => boolean;
  isOccupied?: (date: string) => boolean;
  isPartially?: (date: string) => boolean;
  customClasses?: Record<string, (date: string) => boolean>;
  showWeeksNumber?: boolean;
  singleMonth?: boolean;
  defaultDate?: string;
  selectedDate?: { start: string | null; end: string | null };
  changeDate?: "start" | "end";
  maxDate?: string | null;
  minDate?: string | null;
  firstDayOfWeek?: DayShortcut;
  maxMonthStart?: number;
  maxMonthEnd?: number;
  availableplan?: boolean;
  lang?: Language;
  onInput?: (date: string) => void;
  focusDay: Date;
  onFocusDay: (date: Date) => void;
}

export const VlCalendar: React.FC<VlCalendarProps> = ({
  isSelected,
  isDisabled,
  isOccupied,
  isPartially,
  customClasses,
  showWeeksNumber = false,
  singleMonth = false,
  defaultDate,
  selectedDate,
  changeDate = "start",
  maxDate,
  minDate,
  firstDayOfWeek = "mo",
  maxMonthStart = 2,
  maxMonthEnd = 2,
  availableplan = false,
  lang = "DE",
  onInput,
  focusDay,
  onFocusDay,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    containerRef.current?.focus();
  }, []);

  // Initialize dates
  const initialDate = defaultDate ? parseDate(defaultDate) : getToday();
  const today = getToday();
  const openingtimes = useSelector(
    (state: RootState) => state.stations.openingtimes,
  );
  const startDate = useSelector(
    (state: RootState) => state.userSession.startDate,
  );
  const endDate = useSelector((state: RootState) => state.userSession.endDate);

  // State
  const [currentMonthMonth, setCurrentMonthMonth] = useState(
    initialDate.getMonth(),
  );
  const [currentMonthYear, setCurrentMonthYear] = useState(
    initialDate.getFullYear(),
  );
  const [current, setCurrent] = useState(0);
  const [isHovering, setIsHovering] = useState(false);

  const todayMonthMonth = today.getMonth();
  const todayMonthYear = today.getFullYear();

  // Computed values
  const nextMonthMonth = useMemo(
    () => (currentMonthMonth === 11 ? 0 : currentMonthMonth + 1),
    [currentMonthMonth],
  );

  const nextMonthYear = useMemo(
    () => (currentMonthMonth === 11 ? currentMonthYear + 1 : currentMonthYear),
    [currentMonthMonth, currentMonthYear],
  );

  // Methods
  const moveBack = () => {
    setCurrent((prev) => prev - 1);
    if (currentMonthMonth === 0) {
      setCurrentMonthMonth(11);
      setCurrentMonthYear((prev) => prev - 1);
    } else {
      setCurrentMonthMonth((prev) => prev - 1);
    }
  };

  const moveForward = () => {
    setCurrent((prev) => prev + 1);
    if (currentMonthMonth === 11) {
      setCurrentMonthMonth(0);
      setCurrentMonthYear((prev) => prev + 1);
    } else {
      setCurrentMonthMonth((prev) => prev + 1);
    }
  };

  // Arrow visibility logic
  const showBackArrow = availableplan
    ? current >= -(maxMonthStart - 1)
    : current >= -(maxMonthStart - 1) ||
      currentMonthMonth > todayMonthMonth ||
      currentMonthYear > todayMonthYear;

  const showForwardArrow = singleMonth
    ? current <= maxMonthEnd - 1
    : current <= maxMonthEnd - 2;

  const handleHoveringUpdate = (state: boolean, date: string) => {
    if (!availableplan) {
      setIsHovering(state);
    }
  };

  function handleKeydown(event: any) {
    const focusableSelectors =
      'button:not([disabled]), [tabindex]:is([tabindex="0"])';

    const focusableEls =
      containerRef.current?.querySelectorAll(focusableSelectors);

    const firstEl = focusableEls![0] as HTMLElement;
    const lastEl = focusableEls![focusableEls!.length - 1] as HTMLElement;
    const activeElement = document.activeElement as HTMLElement;
    let newFocusDay = new Date(focusDay);

    switch (event.key) {
      case "ArrowUp":
        newFocusDay = new Date(focusDay);
        newFocusDay.setDate(newFocusDay.getDate() - 7);
        updateFocusDay(newFocusDay);
        event.preventDefault();
        break;
      case "ArrowDown":
        newFocusDay = new Date(focusDay);
        newFocusDay.setDate(newFocusDay.getDate() + 7);
        updateFocusDay(newFocusDay);
        event.preventDefault();
        break;
      case "ArrowLeft":
        newFocusDay = new Date(focusDay);
        newFocusDay.setDate(newFocusDay.getDate() - 1);
        updateFocusDay(newFocusDay);
        event.preventDefault();
        break;
      case "ArrowRight":
        newFocusDay = new Date(focusDay);
        newFocusDay.setDate(newFocusDay.getDate() + 1);
        updateFocusDay(newFocusDay);
        event.preventDefault();
        break;
      case "Enter":
        event.preventDefault();
        // console.log("enter");
        if (!activeElement) return;
        if (activeElement.classList.contains("vl-calendar-month__day")) {
          // this.$emit("input", formateDate(this.focusDay));
        } else {
          if (activeElement.dataset.func) {
            switch (activeElement.dataset.func) {
              case "moveBack":
                (activeElement as HTMLButtonElement).click();
                // this.moveBack();
                break;
              case "moveForward":
                (activeElement as HTMLButtonElement).click();
                // this.moveForward();
                break;
              default:
                (activeElement as HTMLButtonElement).click();
                break;
            }
          } else {
            // console.log(activeElement);
            (activeElement as HTMLButtonElement).click();
          }
        }
        break;
      case "Escape":
        // this.$emit("close");
        event.preventDefault();
        break;
      case "Tab":
        if (event.shiftKey) {
          // shift + tab
          if (document.activeElement === firstEl) {
            event.preventDefault();
            lastEl.focus();
          }
        } else {
          if (document.activeElement === lastEl) {
            event.preventDefault();
            firstEl.focus();
          }
        }
        break;
      default:
        break;
    }
  }

  function updateFocusDay(newFocusDay: any) {
    const min = new Date(minDate!);
    min.setHours(0, 0, 0, 0);
    const max = new Date(maxDate!);
    max.setHours(0, 0, 0, 0);

    if (newFocusDay < min || newFocusDay > max) {
      return;
    }

    if (isDisabled && isDisabled(newFocusDay)) {
      return;
    }

    const focusDay = newFocusDay.setHours(0, 0, 0, 0);

    updateMonth(newFocusDay);
    onFocusDay(new Date(focusDay));
  }

  useEffect(() => {
    const focusableDay = containerRef.current?.querySelector(
      '.orm-calendar-month__day[tabindex="0"]',
    ) as HTMLElement;
    if (focusableDay) {
      focusableDay.focus();
    }
  }, [focusDay]);

  function updateMonth(focusDay: any) {
    if (
      focusDay.getFullYear() < currentMonthYear ||
      (focusDay.getFullYear() === currentMonthYear &&
        focusDay.getMonth() < currentMonthMonth)
    ) {
      moveBack();
    } else if (
      focusDay.getFullYear() > nextMonthYear ||
      (focusDay.getFullYear() === nextMonthYear &&
        focusDay.getMonth() > nextMonthMonth)
    ) {
      moveForward();
    }
  }

  useKeydownListener(containerRef, handleKeydown);

  return (
    <div
      className={`orm-calendar ${
        availableplan
          ? "orm-calendar__availableplan"
          : `orm-calendar__${changeDate}`
      }`}
      role="dialog"
      aria-modal="true"
      aria-label="Calendar"
      tabIndex={availableplan ? -1 : 0}
      ref={containerRef}
    >
      {/* Back Arrow */}
      <button
        className={`orm-calendar__arrow orm-calendar__arrow--back ${
          !showBackArrow ? "disabled" : ""
        }`}
        disabled={!showBackArrow ? true : false}
        onClick={showBackArrow ? moveBack : undefined}
        data-func="moveBack"
      />
      {/* Forward Arrow */}
      <button
        className={`orm-calendar__arrow orm-calendar__arrow--forward ${
          !showForwardArrow ? "disabled" : ""
        }`}
        disabled={!showForwardArrow ? true : false}
        onClick={showForwardArrow ? moveForward : undefined}
        data-func="moveForward"
      />
      {/* Current Month */}
      <VlCalendarMonth
        className="orm-calendar__month"
        month={currentMonthMonth}
        year={currentMonthYear}
        isSelected={isSelected}
        isDisabled={isDisabled}
        isOccupied={isOccupied}
        isPartially={isPartially}
        customClasses={customClasses}
        showWeeksNumber={showWeeksNumber}
        firstDayOfWeek={firstDayOfWeek}
        selectedDate={selectedDate}
        isHovering={isHovering}
        maxDate={maxDate}
        lang={lang}
        onInput={onInput}
        onHoveringUpdate={handleHoveringUpdate}
        focusDay={focusDay}
      />
      {/* Next Month */}
      {!singleMonth && (
        <VlCalendarMonth
          className="orm-calendar__month"
          month={nextMonthMonth}
          year={nextMonthYear}
          isSelected={isSelected}
          isDisabled={isDisabled}
          isOccupied={isOccupied}
          isPartially={isPartially}
          customClasses={customClasses}
          showWeeksNumber={showWeeksNumber}
          firstDayOfWeek={firstDayOfWeek}
          selectedDate={selectedDate}
          isHovering={isHovering}
          maxDate={maxDate}
          lang={lang}
          onInput={onInput}
          onHoveringUpdate={handleHoveringUpdate}
          availableplan={availableplan}
          focusDay={focusDay}
        />
      )}
      <div className="orm-flex orm-calendar-select">
        <div className="orm-flex">
          <div className="orm-mr-8 orm-calendar-select__title"> Von:</div>
          {/* {!availableplan && (
          <div className="">
            {langTranslate("dayof", lang)}{" "}
            <span>
              {changeDate === "start"
                ? langTranslate("pickup", lang)
                : langTranslate("delivery", lang)}
            </span>
            :
          </div>
        )} */}
          <div>
            {" "}
            <button
              className={`orm-calendar-select_date ${changeDate === "start" ? "active" : ""}`}
              onClick={() => onInput!("changetostart")}
            >
              {formatDate(startDate!, "dd.mm.yyyy")}
            </button>{" "}
            <span className="orm-mx-8 orm-calendar-select__title"> Bis:</span>
            <button
              className={`orm-calendar-select_date ${changeDate === "end" ? "active" : ""}`}
              onClick={() => onInput!("changetoend")}
            >
              {formatDate(endDate!, "dd.mm.yyyy")}
            </button>
          </div>
        </div>
        <div className="">
          <button className="orm-btn" onClick={() => onInput!("submit")}>
            Auswählen
          </button>
        </div>
      </div>
    </div>
  );
};

export default VlCalendar;
